import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Popover, Tag, Spin } from 'antd';
import { DashboardOutlined } from '@ant-design/icons';
import api from '../../services/api';

/**
 * HeaderTemperature - leitura atual do termômetro em evidência no header do painel.
 * Mostra um chip com o último valor lido; clicando, abre um Popover que carrega a
 * imagem da leitura (sob demanda, só quando o usuário clica).
 *
 * Robustez: se ainda não houver nenhuma leitura, o componente não renderiza nada
 * (não ocupa espaço nem quebra o header). Falhas de rede são silenciosas.
 *
 * Eficiência: 1 endpoint (GET /api/temperature-readings/latest), polling de 60s +
 * refetch quando a aba volta ao foco (mesmo padrão do HeaderStockTotals).
 */
const POLL_INTERVAL = 60_000;

const fmtValue = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 });

const fmtDateTime = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleString('pt-BR');
  } catch {
    return '';
  }
};

const HeaderTemperature = ({ compact }) => {
  const [reading, setReading] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);
  const imgUrlRef = useRef(null);
  const imgIdRef = useRef(null); // id da leitura da imagem em cache

  const fetchLatest = useCallback(async () => {
    try {
      const res = await api.temperature.latest();
      setReading(res.data?.data || null);
    } catch (err) {
      // Silencioso no header: não incomoda o usuário com toast a cada minuto.
      // eslint-disable-next-line no-console
      console.error('Erro ao carregar temperatura:', err?.response?.data?.error || err.message);
    }
  }, []);

  useEffect(() => {
    fetchLatest();
    const interval = setInterval(fetchLatest, POLL_INTERVAL);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchLatest();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchLatest]);

  // Revoga o object URL da imagem ao desmontar (evita vazamento de memória).
  useEffect(() => {
    return () => {
      if (imgUrlRef.current) URL.revokeObjectURL(imgUrlRef.current);
    };
  }, []);

  // Baixa a imagem somente no clique (abertura do popover); se a leitura for a
  // mesma da última vez, reusa a imagem em cache em vez de baixar de novo.
  const loadImage = useCallback(async (id) => {
    if (!id) return;
    if (imgUrlRef.current && imgIdRef.current === id) return;
    setImgLoading(true);
    try {
      const res = await api.temperature.image(id);
      if (imgUrlRef.current) URL.revokeObjectURL(imgUrlRef.current);
      const url = URL.createObjectURL(res.data);
      imgUrlRef.current = url;
      imgIdRef.current = id;
      setImgUrl(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Erro ao carregar imagem da leitura:', err?.response?.data?.error || err.message);
      setImgUrl(null);
    } finally {
      setImgLoading(false);
    }
  }, []);

  const handleOpenChange = (open) => {
    if (open && reading?.id) loadImage(reading.id);
  };

  // Sem leitura ainda: não renderiza nada.
  if (!reading) return null;

  const isAlert = reading.status === 'alerta';
  const unit = reading.unit || 'C';

  const chipStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    padding: '1px 8px',
    borderRadius: 12,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    color: '#fff',
    background: isAlert ? '#d4380d' : '#0d7dd6',
  };

  const popoverContent = (
    <div style={{ maxWidth: 320 }}>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
        Leitura de {fmtDateTime(reading.occurred_at)}
        {isAlert && (
          <span style={{ color: '#d4380d', fontWeight: 600 }}> · fora da faixa (alerta)</span>
        )}
      </div>
      {imgLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : imgUrl ? (
        <img
          src={imgUrl}
          alt={`Imagem da leitura do termômetro de ${fmtDateTime(reading.occurred_at)}`}
          style={{ width: '100%', borderRadius: 6, background: '#fff', border: '1px solid #eee' }}
        />
      ) : (
        <div style={{ fontSize: 12, color: '#9ca3af', padding: 12 }}>
          Imagem indisponível para esta leitura.
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={popoverContent}
      title="Temperatura atual"
      trigger="click"
      placement="bottomRight"
      onOpenChange={handleOpenChange}
    >
      <Tag
        style={chipStyle}
        role="status"
        aria-label={`Temperatura atual: ${fmtValue(reading.value)} graus ${unit}${isAlert ? ', em alerta (fora da faixa)' : ''}. Clique para ver a imagem da leitura.`}
      >
        <DashboardOutlined />
        <span>{compact ? 'Temp:' : 'Termômetro:'}</span>
        <span>{fmtValue(reading.value)}°{unit}</span>
      </Tag>
    </Popover>
  );
};

export default HeaderTemperature;
